import { FC, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { updateProfile } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { CustomTextfield } from '@/components/CustomTextfield/styles';
import { LeftSide, SettingBox, StyledForm, Subtitle, WhiteWrapper } from '../styles';
import { AccountSettingsSchema, getAccountSettingsSchema } from '../../schema';
import UploadAvatar from './UploadAvatar';
import { ButtonWrapper } from '../../styles';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateUser } from '@/services/noteGoApi/noteGoApiComponents';
import { useAuthStore } from '@/stores/authStore';
import { isAvatarChanged, isNameOrNumberChanged, setUserValues } from '../../helpers/formHelpers';
import useAlertStore from '@/stores/alertStore';
import { errorReporter } from '@/services/errorReportingService';
import { userStorage } from '@/firebase/firebase';
import getGravatarUrl from '@/helpers/getGravatarUrl';
import useAvatarStore from '@/stores/changeAvatarStore';

const PersonalInformation: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { showAlert } = useAlertStore();
  const { setAvatarChanged } = useAvatarStore();
  const { userToken, currentUser, userData, fetchAndSetUserData } = useAuthStore();
  const { mutateAsync: updateUser } = useUpdateUser();

  const accountSettingsSchema = getAccountSettingsSchema({
    required: 'This field is required',
    invalidPhoneNumber: 'Invalid phone number format',
  });

  const { control, formState, handleSubmit, setValue } = useForm<AccountSettingsSchema>({
    resolver: zodResolver(accountSettingsSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      avatar: '',
    },
  });

  useEffect(() => {
    fetchAndSetUserData();
  }, []);

  useEffect(() => {
    if (userData) {
      setUserValues(
        { ...userData, photoURL: currentUser?.photoURL || getGravatarUrl(currentUser?.email || '') || '' },
        setValue,
      );
    }
  }, [userData, setValue, currentUser]);

  const { errors } = formState;

  const handleReset = () => {
    setUserValues({ ...userData, photoURL: currentUser?.photoURL || '' }, setValue);
  };

  const onSubmit: SubmitHandler<AccountSettingsSchema> = async (data) => {
    if (!userData || !currentUser) return;
    setIsLoading(true);
    let avatarUrl: string | undefined = undefined;

    try {
      if (isAvatarChanged({ ...userData, photoURL: currentUser?.photoURL || '' }, data)) {
        const file = data.avatar && data.avatar instanceof File ? data.avatar : null;

        if (file) {
          const avatarRef = ref(userStorage, `${userData.id}/avatar.jpg`);
          await uploadBytes(avatarRef, file);
          avatarUrl = await getDownloadURL(avatarRef);
        }
      }

      if (isNameOrNumberChanged({ ...userData, photoURL: currentUser?.photoURL || '' }, data)) {
        await updateUser({
          pathParams: { userId: userData.id },
          body: {
            first_name: data.firstName,
            last_name: data.lastName,
            phone_number: data.phoneNumber,
          },
          headers: { authorization: `Bearer ${userToken}` },
        });
        await updateProfile(currentUser, {
          displayName: `${data.firstName} ${data.lastName}`,
        });
      }

      if (avatarUrl) {
        await updateProfile(currentUser, {
          photoURL: avatarUrl,
        });
      }

      showAlert('Personal information updated successfully', 'success');
      setAvatarChanged(true);
    } catch (err) {
      errorReporter.expected('PersonalInformation: Failed to update personal information', err, {
        feature: 'settings',
        operation: 'updatePersonalInformation',
      });
      showAlert('Failed to update personal information', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SettingBox>
      <LeftSide>
        <Typography variant="body1" fontWeight="700">
          Personal Info
        </Typography>
        <Subtitle variant="body2">You can change your personal information settings here.</Subtitle>
      </LeftSide>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Stack>
          <WhiteWrapper>
            <Stack flexDirection="row" gap="1.5rem" mb="1.5rem">
              <Stack gap="0.5rem" width="100%">
                <Typography variant="body2" fontWeight="700">
                  First Name
                </Typography>

                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      variant="outlined"
                      size="small"
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                    />
                  )}
                />
              </Stack>

              <Stack gap="0.5rem" width="100%">
                <Typography variant="body2" fontWeight="700">
                  Last Name
                </Typography>

                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      variant="outlined"
                      size="small"
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                    />
                  )}
                />
              </Stack>
            </Stack>

            <Stack gap="0.5rem" width="100%" mb="1.5rem">
              <Typography variant="body2" fontWeight="700">
                Phone Number{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  (Optional)
                </Typography>
              </Typography>
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    variant="outlined"
                    size="small"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                  />
                )}
              />
            </Stack>

            <UploadAvatar control={control} />
          </WhiteWrapper>
          <ButtonWrapper>
            <Button type="reset" onClick={handleReset} variant="outlined" color="primary" disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isLoading}>
              {!isLoading ? 'Save' : <CircularProgress size={24} />}
            </Button>
          </ButtonWrapper>
        </Stack>
      </StyledForm>
    </SettingBox>
  );
};

export default PersonalInformation;
